import {defineStore} from "pinia";
import {ref} from "vue";
import {Campaign} from "@src/types/Campaign";
import Channel from "@src/types/Channel";
import {CampaignTemplate} from "@src/types/CampaignTemplate";
import WaapiService from "@src/services/waapi"
import {useAuthStore} from "@src/stores/auth";
import ChatSession from "@src/types/ChatSession";

export const useWAAPIStore = defineStore('waapi', {
    state: () => ({
        showCampaignModal: ref<boolean>(false),
        campaignForm: ref<Campaign>({
            id: 0,
            model_id: 0,
            channel_id: 0,
            transferred_operator_id: null,
            user_id: null,
            name: '',
            status: '',
            scheduled: 'immediate',
            scheduled_at: new Date(),
            sent_at: new Date(),
            single: true,
            via_api: false,
            company_id: 0,
            components: [],
        }),
        selectedChannel: ref<Channel>({
            id: 0,
            name: '',
            type: '',
            token: '',
            status: '',
        }),
        selectedTemplate: ref<CampaignTemplate>({
            id: 0,
            channel_id: 0,
            department_id: 0,
            name: '',
            parameter_format: '',
            components: [],
            variables: '',
            language: '',
            status: '',
            reason: '',
            category: '',
        }),
        selectedFile: ref<File | null>(null),
        filePreview: ref<string | null>(null),
        step: ref<string>('info'),
        loading: ref<boolean>(false),
        costumerToken: ref<string | null>(null),
        bulk: ref<boolean>(false),
        sessions: ref<ChatSession[]>([])
    }),
    getters: {},
    actions: {
        selectTemplate(template: CampaignTemplate) {
            this.resetForm()
            this.selectedTemplate = template
            this.campaignForm.model_id = template.id
        },
        async saveCampaign() {
            const store = useAuthStore()

            this.loading = true
            const campaign = this.campaignForm
            const componentsMap: Record<string, any> = {}
            for (const component of campaign.components) {
                const key = component.type.toLowerCase()
                if (key === 'buttons') {
                    componentsMap['buttons'] = component.buttons
                } else {
                    componentsMap[key] = component
                }
            }
            const data = new FormData();
            data.append('id', campaign.id.toString());
            data.append('name', campaign.name);
            data.append('model', campaign.model_id.toString());
            data.append('channel', campaign.channel_id.toString());
            data.append('scheduled', campaign.scheduled);

            if (campaign.scheduled === 'scheduled') {
                data.append('scheduled_at', campaign.scheduled_at.toISOString());
                if (campaign.transferred_operator_id && campaign.transferred_operator_id !== store.user?.id) {
                    data.append('transferred_operator', campaign.transferred_operator_id.toString())
                }
            }

            data.append('step', this.step);
            if (this.selectedFile) data.append('header', this.selectedFile);
            data.append('components', JSON.stringify(componentsMap))
            const response = await WaapiService.saveCampaign(data)
            this.campaignForm.id = response.campaign.id;
            this.loading = false
        },

        async nextStep() {
            await this.saveCampaign()
            let hasButtons = this.selectedTemplate.components.some((c: any) => c.type === 'BUTTONS')
            let hasHeader = this.selectedTemplate.components.some((c: any) => c.type === 'HEADER')
            if (this.step === 'info' && hasHeader) {
                const headerComponent = this.selectedTemplate.components.find((c: any) => c.type === 'HEADER')
                if (headerComponent && headerComponent.format === 'TEXT') {
                    if (hasButtons) {
                        return this.step = 'buttons'
                    }
                    return this.step = 'review'
                }
                this.step = 'header'
            } else if ((this.step === 'info' || this.step === 'header') && hasButtons) {
                this.step = 'buttons'
            } else {
                this.step = 'review'
            }
        },

        previousStep() {
            let hasButtons = this.selectedTemplate.components.some((c: any) => c.type === 'BUTTONS')
            let hasHeader = this.selectedTemplate.components.some((c: any) => c.type === 'HEADER')
            if (this.step === 'review' && hasButtons) {
                this.step = 'buttons'
            } else if ((this.step === 'review' || this.step === 'buttons') && hasHeader) {
                const headerComponent = this.selectedTemplate.components.find((c: any) => c.type === 'HEADER')
                if (headerComponent && headerComponent.format === 'TEXT') {
                    return this.step = 'info'
                }
                this.step = 'header'
            } else {
                this.step = 'info'
            }
        },
        resetForm() {
            let campaignName = this.campaignForm.name
            let campaignId = this.campaignForm.id
            let channelId = this.campaignForm.channel_id
            let operatorId = this.campaignForm.transferred_operator_id
            let schedule = this.campaignForm.scheduled
            let scheduledAt = this.campaignForm.scheduled_at
            let userId = this.campaignForm.user_id
            this.costumerToken = null
            this.step = 'info'
            this.filePreview = null
            this.selectedFile = null
            this.selectedTemplate = {
                id: 0,
                channel_id: 0,
                department_id: 0,
                name: '',
                parameter_format: '',
                components: [],
                variables: '',
                language: '',
                status: '',
                reason: '',
                category: '',
            }
            this.campaignForm = {
                id: campaignId,
                model_id: 0,
                channel_id: channelId,
                user_id: userId,
                transferred_operator_id: operatorId,
                scheduled: schedule,
                name: campaignName,
                status: '',
                scheduled_at: scheduledAt,
                sent_at: new Date(),
                single: true,
                via_api: false,
                company_id: 0,
                components: [],
            }
        },
        cleanCampaign() {
            this.costumerToken = null
            this.step = 'info'
            this.filePreview = null
            this.selectedFile = null
            this.sessions = []
            this.bulk = false
            this.selectedTemplate = {
                id: 0,
                channel_id: 0,
                department_id: 0,
                name: '',
                parameter_format: '',
                components: [],
                variables: '',
                language: '',
                status: '',
                reason: '',
                category: '',
            }
            this.campaignForm = {
                id: 0,
                model_id: 0,
                channel_id: 0,
                user_id: 0,
                transferred_operator_id: 0,
                scheduled: 'immediate',
                name: '',
                status: '',
                scheduled_at: new Date(),
                sent_at: new Date(),
                single: true,
                via_api: false,
                company_id: 0,
                components: [],
            }
        },
        toHtml(text: string) {
            return text
                .replace(/{{\s*(\d+|[a-zA-Z_][a-zA-Z0-9_]*)\s*}}/g, (_, varName) => {
                    const isNumber = /^\d+$/.test(varName);
                    return `<span class="variable ${isNumber ? 'numeric' : 'named'}">{{ ${varName} }}</span>`;
                })
                .replace(/```(.+?)```/gs, '<span style="font-family: monospace; background-color: #f4f4f4; padding: 2px 4px;">$1</span>')
                .replace(/\*(.+?)\*/g, '<span style="font-weight: bold;">$1</span>')
                .replace(/_([^_]+)_/g, '<span style="font-style: italic;">$1</span>')
                .replace(/~(.+?)~/g, '<span style="text-decoration: line-through;">$1</span>');
        },
    }
})